<script>
    import { createEventDispatcher } from "svelte";
    import { Section } from "../../lib/ui";
    import {
        THEME_TOKENS,
        THEME_PRESETS,
        DEFAULT_THEME,
        GAME_FONTS,
        BORDER_STYLES,
        OUTLINE_TEXT,
    } from "./gameSlots.js";

    /**
     * UI 색. 서버 MAIN 컬럼 안(`main.theme`)에 들어간다 — 화면 구성과 같은 덩어리다.
     *
     * ⚠️ 봇 빌더 [디자인] 탭(PageDesign.svelte)과 **다른 저장소**다. 그쪽은 CSS 를 굽는
     *    채팅/기업형 페이지용(BOT_DESIGN)이고, 게임은 캔버스라 CSS 가 안 먹는다.
     *    색 피커·타일 같은 **화면 관용구만** 그대로 가져왔다.
     */
    export let theme = {};
    /** 글꼴 키(예: 'Katuri'). 빈 값이면 기본 산세리프. `main.font` 에 들어간다. */
    export let font = "";

    const dispatch = createEventDispatcher();

    // 저작자가 안 칠한 칸은 기본값으로 보여준다(런타임도 같은 값으로 떨어진다).
    $: current = { ...DEFAULT_THEME, ...(theme || {}) };

    const emit = (next) => dispatch("change", next);

    const applyPreset = (preset) => emit({ ...preset.theme });

    /**
     * ⚠️ toolcool-color-picker 는 `on:change` 를 매 픽셀 드래그마다 쏜다 —
     *    그대로 저장하면 색 하나 고르는 동안 요청이 수십 번 나간다. PageDesign 과 같이
     *    **`on:mouseup`(손을 뗐을 때)** 에만 반영한다.
     */
    const changeColor = (key) => (event) => {
        const picked = event.target?.color?.toHexString?.();
        if (!picked) return;
        // 피커는 8자리(#rrggbbaa)를 줄 수 있다. 런타임은 6자리만 받는다(theme.ts HEX_RE).
        emit({ ...current, [key]: picked.slice(0, 7).toLowerCase() });
    };

    const setBorderStyle = (value) => emit({ ...current, borderStyle: value });
    const setOutlineText = (on) => emit({ ...current, outlineText: on });

    /**
     * 지금 설정이 어느 프리셋과 똑같은지 — 맞으면 그 타일을 켠 것으로 보여준다.
     * ⚠️ 테두리 방식도 함께 본다. 색만 비교하면 테두리를 바꿔도 프리셋이 켜진 채라 헷갈린다.
     */
    const isPresetOn = (preset) =>
        current.borderStyle === preset.theme.borderStyle &&
        (current.outlineText ?? false) === preset.theme.outlineText &&
        THEME_TOKENS.every((t) => current[t.key] === preset.theme[t.key]);

    const resetAll = () => emit({});

    $: changed =
        current.borderStyle !== DEFAULT_THEME.borderStyle ||
        (current.outlineText ?? false) !== DEFAULT_THEME.outlineText ||
        THEME_TOKENS.some((t) => current[t.key] !== DEFAULT_THEME[t.key]);
</script>

<svelte:head>
    <script
        src="https://cdn.jsdelivr.net/npm/toolcool-color-picker/dist/toolcool-color-picker.min.js"
    ></script>
</svelte:head>

<Section
    title="글꼴"
    description="게임 화면 전체(메인·플레이·결과)가 이 글꼴로 나옵니다. 봇 빌더 [디자인] 탭과 같은 목록입니다."
>
    <div class="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3">
        {#each GAME_FONTS as f (f.key)}
            <button
                type="button"
                class="gd-tile"
                class:gd-tile-on={(font || "") === f.key}
                on:click={() => dispatch("font", f.key)}
            >
                <!-- 미리보기는 그 글꼴로 직접 그린다 — 이름만 보고는 고를 수 없다.
                     (@font-face 는 빌더 전역 CSS 에 이미 있다: avatalk-builder.webflow.css) -->
                <span
                    class="gd-font-sample"
                    style={f.key ? `font-family:'${f.key}', sans-serif;` : "font-family:sans-serif;"}
                >
                    {f.sample}
                </span>
                <span class="gd-tile-label">{f.label}</span>
            </button>
        {/each}
    </div>

    <p class="tw-mt-3 tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
        게임은 캔버스라 글꼴을 <strong class="tw-text-foreground">다 받은 뒤에 시작</strong>합니다 —
        글꼴이 크면 첫 진입이 아주 잠깐 늦어질 수 있습니다.
    </p>
</Section>

<Section
    title="색 프리셋"
    description="게임 화면의 알약·카드·팝업·버튼이 쓰는 색입니다. 먼저 묶음을 고르고, 필요하면 아래에서 낱개로 바꾸세요."
>
    <div class="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3">
        {#each THEME_PRESETS as p (p.key)}
            <button
                type="button"
                class="gd-tile"
                class:gd-tile-on={isPresetOn(p)}
                on:click={() => applyPreset(p)}
            >
                <!-- 색 자체를 보여주는 게 이름보다 빠르다 — 작은 화면 모형으로 깐다. -->
                <span
                    class="gd-swatch"
                    style="background:{p.theme.panel}; border-color:{p.theme.border};"
                >
                    <span class="gd-swatch-bar" style="background:{p.theme.accent};"></span>
                    <span class="gd-swatch-text" style="color:{p.theme.text};">가</span>
                </span>
                <span class="gd-tile-label">{p.label}</span>
                <span class="gd-tile-sub">{p.sample}</span>
            </button>
        {/each}
    </div>
</Section>

<Section
    title="테두리"
    description="알약·카드·버튼의 외곽선을 어떻게 그을지입니다. 색이 아니라 선을 긋는 방식이라 따로 둡니다."
>
    <div class="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-4">
        {#each BORDER_STYLES as b (b.value)}
            <button
                type="button"
                class="gd-tile"
                class:gd-tile-on={current.borderStyle === b.value}
                on:click={() => setBorderStyle(b.value)}
            >
                <!-- 말로는 차이가 안 와닿는다 — 실제로 그 방식으로 그린 사각형을 보여준다.
                     (런타임은 Phaser 로 긋지만 원리는 같다: 경로를 흔들어 두 번 겹친다) -->
                <span class="gd-border-sample">
                    {#if b.value === "clean"}
                        <svg viewBox="0 0 120 44" width="100%" height="44">
                            <rect x="3" y="3" width="114" height="38" rx="12"
                                fill="none" stroke="currentColor" stroke-width="2" />
                        </svg>
                    {:else if b.value === "sketch"}
                        <svg viewBox="0 0 120 44" width="100%" height="44">
                            <path d="M15 4.4 C40 3.2, 80 4.8, 105 4 C113 5.6, 116.4 9, 116 15
                                     C116.8 25, 115.2 32, 116 37 C114.4 40.4, 111 42.8, 105 40.4
                                     C80 41.6, 40 40, 15 40.8 C7 39.2, 3.6 35, 4 29
                                     C3.2 19, 4.8 12, 4 7 C5.6 3.6, 9 1.2, 15 4.4 Z"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                            <path d="M16 6 C41 4.6, 79 6.2, 104 5.4 C112 7, 115 10.4, 114.6 16.4
                                     C115.4 26.4, 113.8 33, 114.6 38 C113 41.4, 109.6 43, 104 41.8
                                     C79 43, 41 41.4, 16 42.2 C8 40.6, 5 36.4, 5.4 30.4
                                     C4.6 20.4, 6.2 13.4, 5.4 8.4 C7 5, 10.4 3.4, 16 6 Z"
                                fill="none" stroke="currentColor" stroke-width="1.4"
                                stroke-linecap="round" opacity="0.75" />
                        </svg>
                    {:else if b.value === "brushStraight"}
                        <!-- 시안 방식: 경로는 반듯하고 획만 붓. 가장자리가 자잘하게 거칠다. -->
                        <svg viewBox="0 0 120 44" width="100%" height="44">
                            <rect x="4" y="4" width="112" height="36" rx="12"
                                fill="none" stroke="currentColor" stroke-width="4.6"
                                stroke-linecap="round" opacity="0.9"
                                stroke-dasharray="26 1.4 17 1 31 1.6 22 1.2" />
                            <rect x="4" y="4" width="112" height="36" rx="12"
                                fill="none" stroke="currentColor" stroke-width="2.2"
                                stroke-linecap="round" opacity="0.5"
                                stroke-dasharray="9 2 14 1.5 6 2.5" />
                        </svg>
                    {:else}
                        <!-- 손그림 경로 + 붓. 선이 굽이치면서 붓 질감이 얹힌다. -->
                        <svg viewBox="0 0 120 44" width="100%" height="44">
                            <path d="M14 4 C40 2.6, 78 5.2, 104 3.6 C113 5.4, 117 9.6, 116 16"
                                fill="none" stroke="currentColor" stroke-width="4.2" stroke-linecap="round" />
                            <path d="M116.6 24 C117 31, 115 35.6, 116 38.6 C113 41.6, 108 43.4, 101 41"
                                fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
                            <path d="M92 41.6 C70 42.6, 38 40.4, 17 41.4 C8 39.4, 3.6 35, 4.4 28.6"
                                fill="none" stroke="currentColor" stroke-width="3.6" stroke-linecap="round" />
                            <path d="M4 20 C3.4 14, 5 9, 4.4 6.6"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                        </svg>
                    {/if}
                </span>
                <span class="gd-tile-label">{b.label}</span>
                <span class="gd-tile-sub">{b.hint}</span>
            </button>
        {/each}
    </div>

    <label class="tw-mt-4 tw-flex tw-items-start tw-gap-2 tw-text-[13px] tw-text-foreground">
        <input
            type="checkbox"
            checked={current.outlineText ?? false}
            on:change={(e) => setOutlineText(e.target.checked)}
        />
        <span class="tw-flex tw-flex-col">
            <span>{OUTLINE_TEXT.label}</span>
            <span class="tw-text-[11px] tw-leading-tight tw-text-muted-foreground">
                {OUTLINE_TEXT.hint}
            </span>
        </span>
    </label>
</Section>

<Section title="색 낱개로" description="프리셋에서 시작해 필요한 칸만 바꾸면 됩니다.">
    <div class="tw-grid tw-gap-2 sm:tw-grid-cols-2">
        {#each THEME_TOKENS as t (t.key)}
            <div class="gd-color-row">
                <toolcool-color-picker
                    class="color-target"
                    color={current[t.key]}
                    button-width="3.4rem"
                    button-height="2rem"
                    button-padding="2px"
                    on:mouseup={changeColor(t.key)}
                />
                <div class="tw-flex tw-min-w-0 tw-flex-col">
                    <span class="tw-text-sm tw-text-foreground">{t.label}</span>
                    {#if t.hint}
                        <span class="tw-text-[11px] tw-leading-tight tw-text-muted-foreground">
                            {t.hint}
                        </span>
                    {/if}
                </div>
                <span class="tw-ml-auto tw-font-mono tw-text-[11px] tw-text-muted-foreground">
                    {current[t.key]}
                </span>
            </div>
        {/each}
    </div>

    <p class="tw-mt-4 tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
        ⚠️ 플레이 중 화면(점수·목숨)은 이 색을 쓰지 않습니다. 거기 글자는 팝업 위가 아니라
        <strong class="tw-text-foreground">흐르는 배경 그림 위에 바로</strong> 얹혀서, 배경이 무엇이든
        읽히도록 따로 맞춰져 있습니다.
    </p>

    {#if changed}
        <div class="tw-mt-4 tw-border-t tw-border-border tw-pt-4">
            <button
                type="button"
                class="tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-transparent tw-px-3.5 tw-py-2 tw-text-xs tw-text-muted-foreground tw-transition-colors hover:tw-text-foreground"
                on:click={resetAll}
            >
                기본값으로 되돌리기
            </button>
        </div>
    {/if}
</Section>

<style>
    /* PageDesign 의 pd-tile 과 같은 모양 — 빌더끼리 생김새가 갈리지 않게. */
    .gd-tile {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 12px;
        border: 2px solid hsl(var(--sb-border));
        border-radius: 12px;
        background: hsl(var(--sb-card));
        cursor: pointer;
        transition:
            border-color 0.15s,
            box-shadow 0.15s;
    }
    .gd-tile:hover {
        border-color: hsl(var(--sb-primary) / 0.5);
    }
    .gd-tile-on {
        border-color: hsl(var(--sb-primary));
        box-shadow: 0 0 0 1px hsl(var(--sb-primary)) inset;
    }
    .gd-tile-label {
        font-size: 13px;
        font-weight: 600;
        color: hsl(var(--sb-foreground));
    }
    .gd-tile-sub {
        font-size: 11px;
        color: hsl(var(--sb-muted-foreground));
    }

    /* 프리셋 미리보기 — 알약 하나 + 강조 막대 + 글자. 실제 화면의 축소판이다. */
    .gd-swatch {
        position: relative;
        display: flex;
        width: 100%;
        height: 46px;
        align-items: center;
        justify-content: center;
        gap: 6px;
        border: 2px solid;
        border-radius: 10px;
    }
    .gd-swatch-bar {
        width: 34px;
        height: 8px;
        border-radius: 4px;
    }
    .gd-swatch-text {
        font-size: 15px;
        font-weight: 700;
    }

    /* 테두리 미리보기 — 글자색을 따라가게 해서 라이트/다크 모두에서 보이게. */
    .gd-border-sample {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: center;
        color: hsl(var(--sb-foreground));
    }

    /* 글꼴 미리보기 — 글자 크기를 키워야 생김새 차이가 보인다. */
    .gd-font-sample {
        display: flex;
        height: 40px;
        width: 100%;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        white-space: nowrap;
        font-size: 19px;
        color: hsl(var(--sb-foreground));
    }

    .gd-color-row {
        display: flex;
        align-items: center;
        gap: 12px;
        border: 1px solid hsl(var(--sb-border));
        border-radius: 8px;
        background: hsl(var(--sb-muted) / 0.3);
        padding: 8px 12px;
    }
</style>
